import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  FileText,
  Database,
  Users,
  Building2,
  FolderOpen,
  Search,
  TrendingUp,
  Upload,
  Bell,
  Clock,
  ArrowUpRight,
  Sparkles,
  BookOpen,
  Beaker,
  FileCode,
  Scale,
  Microscope,
  Image,
  FileQuestion,
  GraduationCap,
  FileBarChart,
} from 'lucide-react';
import { researchService } from '@/services/api/research';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from 'recharts';

const RESOURCE_TYPE_ICONS: Record<string, React.ReactNode> = {
  article: <BookOpen className="h-4 w-4" />,
  dataset: <Database className="h-4 w-4" />,
  software: <FileCode className="h-4 w-4" />,
  patent: <Scale className="h-4 w-4" />,
  lab_tool: <Beaker className="h-4 w-4" />,
  manual: <FileText className="h-4 w-4" />,
  image: <Image className="h-4 w-4" />,
  thesis: <GraduationCap className="h-4 w-4" />,
  report: <FileBarChart className="h-4 w-4" />,
  grant_proposal: <FileQuestion className="h-4 w-4" />,
};

const COLORS = ['#667eea', '#764ba2', '#48bb78', '#ed8936', '#f687b3', '#4299e1', '#9f7aea', '#38b2ac'];

export function ResearchDashboardPage() {
  const navigate = useNavigate();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['research-dashboard-stats'],
    queryFn: () => researchService.getDashboardStats(),
    refetchInterval: 60000,
  });

  const { data: trendingTopics } = useQuery({
    queryKey: ['research-trending-topics'],
    queryFn: () => researchService.getTrendingTopics(),
  });

  const { data: recentActivity } = useQuery({
    queryKey: ['research-recent-activity'],
    queryFn: () => researchService.getRecentActivity(),
  });

  // Chart data
  const uploadTrendData = [
    { month: 'Sep', uploads: 89 },
    { month: 'Oct', uploads: 112 },
    { month: 'Nov', uploads: 98 },
    { month: 'Dec', uploads: 134 },
    { month: 'Jan', uploads: 145 },
    { month: 'Feb', uploads: 167 },
    { month: 'Mar', uploads: 156 },
    { month: 'Apr', uploads: stats?.uploadsThisMonth || 156 },
  ];

  const resourceTypeData = stats?.resourcesByType
    ? Object.entries(stats.resourcesByType).map(([type, count]) => ({
        name: type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
        value: count,
        type,
      }))
    : [];

  const searchActivityData = [
    { day: 'Mon', searches: 890 },
    { day: 'Tue', searches: 756 },
    { day: 'Wed', searches: 923 },
    { day: 'Thu', searches: 845 },
    { day: 'Fri', searches: 1102 },
    { day: 'Sat', searches: 567 },
    { day: 'Sun', searches: 595 },
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'upload':
        return <Upload className="h-4 w-4 text-blue-500" />;
      case 'citation':
        return <BookOpen className="h-4 w-4 text-green-500" />;
      case 'download':
        return <TrendingUp className="h-4 w-4 text-purple-500" />;
      case 'collaboration':
        return <Users className="h-4 w-4 text-orange-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            Research Intelligence Dashboard
          </h1>
          <p className="text-muted-foreground">
            Australian National Research Data Center - Unified Research Discovery Platform
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/panel/research/resources')}>
            <Database className="mr-2 h-4 w-4" />
            Browse Resources
          </Button>
          <Button onClick={() => navigate('/panel/research/resources')}>
            <Upload className="mr-2 h-4 w-4" />
            Upload Resource
          </Button>
        </div>
      </div>

      {/* Semantic Search Bar */}
      <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-purple-500/5">
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="font-semibold">AI-Powered Semantic Search</span>
              <Badge variant="secondary">Natural Language</Badge>
            </div>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Ask anything... e.g., 'Show me climate change datasets from 2020-2024 with temperature measurements'"
                  className="pl-10 h-12 text-base"
                />
              </div>
              <Button size="lg" className="h-12 px-8">
                Search
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-muted-foreground">Try:</span>
              <Badge variant="outline" className="cursor-pointer hover:bg-primary/10">
                "Find experts in quantum computing"
              </Badge>
              <Badge variant="outline" className="cursor-pointer hover:bg-primary/10">
                "Datasets using machine learning"
              </Badge>
              <Badge variant="outline" className="cursor-pointer hover:bg-primary/10">
                "Research funded by ARC"
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/panel/research/resources')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Resources</p>
                {statsLoading ? (
                  <Skeleton className="h-8 w-20 mt-1" />
                ) : (
                  <p className="text-3xl font-bold">{stats?.totalResources.toLocaleString()}</p>
                )}
                <p className="text-xs text-green-600 flex items-center mt-1">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  +{stats?.recentUploads || 47} this week
                </p>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-500/10">
                <Database className="h-7 w-7 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/panel/research/researchers')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Researchers</p>
                {statsLoading ? (
                  <Skeleton className="h-8 w-20 mt-1" />
                ) : (
                  <p className="text-3xl font-bold">{stats?.totalResearchers.toLocaleString()}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">Across all institutions</p>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-purple-500/10">
                <Users className="h-7 w-7 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/panel/research/institutions')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Institutions</p>
                {statsLoading ? (
                  <Skeleton className="h-8 w-20 mt-1" />
                ) : (
                  <p className="text-3xl font-bold">{stats?.totalInstitutions.toLocaleString()}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">Partner organizations</p>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-green-500/10">
                <Building2 className="h-7 w-7 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/panel/research/workspaces')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Workspaces</p>
                {statsLoading ? (
                  <Skeleton className="h-8 w-20 mt-1" />
                ) : (
                  <p className="text-3xl font-bold">{stats?.totalWorkspaces.toLocaleString()}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">Active projects</p>
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-orange-500/10">
                <FolderOpen className="h-7 w-7 text-orange-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/panel/research/quality-assurance')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Quality Score</p>
                {statsLoading ? (
                  <Skeleton className="h-8 w-20 mt-1" />
                ) : (
                  <p className="text-3xl font-bold">{stats?.avgMetadataCompleteness}%</p>
                )}
                <Progress value={stats?.avgMetadataCompleteness || 87} className="h-1.5 mt-2" />
              </div>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-pink-500/10">
                <Microscope className="h-7 w-7 text-pink-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Upload Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Resource Upload Trends
            </CardTitle>
            <CardDescription>Monthly uploads over the past 8 months</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={uploadTrendData}>
                  <defs>
                    <linearGradient id="uploadGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#667eea" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#667eea" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="month" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="uploads"
                    stroke="#667eea"
                    strokeWidth={2}
                    fill="url(#uploadGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Resource Types Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" />
              Resources by Type
            </CardTitle>
            <CardDescription>Distribution of research resources across categories</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] flex items-center">
              <ResponsiveContainer width="50%" height="100%">
                <PieChart>
                  <Pie
                    data={resourceTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {resourceTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {resourceTypeData.slice(0, 6).map((item, index) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-sm flex-1">{item.name}</span>
                    <span className="text-sm font-medium">{item.value.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Row */}
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Search Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5 text-primary" />
              Search Activity
            </CardTitle>
            <CardDescription>Searches this week: {stats?.searchesThisWeek?.toLocaleString()}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={searchActivityData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="day" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="searches" fill="#764ba2" radius={[4, 4, 0, 0]} activeBar={false}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Trending Topics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Trending Research Topics
            </CardTitle>
            <CardDescription>Most searched topics this month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {trendingTopics?.slice(0, 6).map((topic, index) => (
                <div key={topic.topic} className="flex items-center gap-3">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                    {index + 1}
                  </span>
                  <span className="flex-1 text-sm font-medium">{topic.topic}</span>
                  <Badge variant="secondary" className="text-xs">
                    {topic.count.toLocaleString()}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" />
              Recent Activity
            </CardTitle>
            <CardDescription>Latest updates across the platform</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity?.map((activity) => (
                <div key={activity.id} className="flex items-start gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{activity.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Access Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 transition-all" onClick={() => navigate('/panel/research/image-search')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-pink-500 to-purple-500">
                <Image className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Visual Search</h3>
                <p className="text-sm text-muted-foreground">Search by uploading images</p>
              </div>
              <ArrowUpRight className="ml-auto h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 transition-all" onClick={() => navigate('/panel/research/citations')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500">
                <BookOpen className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Citations</h3>
                <p className="text-sm text-muted-foreground">Generate & track citations</p>
              </div>
              <ArrowUpRight className="ml-auto h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 transition-all" onClick={() => navigate('/panel/research/researchers')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-green-500 to-teal-500">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Find Experts</h3>
                <p className="text-sm text-muted-foreground">Connect with researchers</p>
              </div>
              <ArrowUpRight className="ml-auto h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md hover:border-primary/50 transition-all" onClick={() => navigate('/panel/research/search-history')}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-red-500">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold">Search History</h3>
                <p className="text-sm text-muted-foreground">View past searches</p>
              </div>
              <ArrowUpRight className="ml-auto h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
